import { type TUser } from '@/types/models';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { getSearchRedirect } from '@/services/api.ts';

export type AuthAction = 'sign-in' | 'sign-up' | 'forgot-password' | 'reset-password';

export type SignupForm = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  profession: string;
  profession_other: string;
  company_name: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  password: string;
};

const initState = {
  token: undefined as undefined | string,
  user: undefined as undefined | TUser,
  showAuth: false,
  redirectUrl: undefined as undefined | string,
  action: 'sign-in' as AuthAction,
  signUpStep: 0 as number,
  signupForm: {} as SignupForm,
};
type State = typeof initState;

export const useAuthStore = create<State>()(
  persist(immer(devtools(() => initState, { enabled: true, name: 'auth-store', trace: true, traceLimit: 25 })), { name: 'auth-store' }),
);

export function useAuth() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) {
      // noinspection JSIgnoredPromiseFromCall
      navigate({ to: '/login/$', search: {redirect: getSearchRedirect()} });
    }
  }, [user]);
  return { user: user as TUser };
}

export function setShowAuth(set: boolean, redirectUrl?: string) {
  return useAuthStore.setState((state) => {
    state.showAuth = set;
    state.redirectUrl = redirectUrl;
    if (!state.showAuth) {
      state.signUpStep = 0;
    }
  });
}

export function setAuthAction(action: AuthAction) {
  return useAuthStore.setState((state) => {
    state.action = action;
  });
}

export function setSignupStep(next: boolean = true) {
  return useAuthStore.setState((state) => {
    if (next && state.signUpStep < 3) {
      state.signUpStep = state.signUpStep + 1;
    }
    if (!next && state.signUpStep > 0) {
      state.signUpStep = state.signUpStep - 1;
    }
  });
}

export function updateSignupForm(values: Partial<SignupForm>) {
  return useAuthStore.setState((state) => {
    state.signupForm = { ...state.signupForm, ...values };
  });
}

export function resetSignupForm() {
  return useAuthStore.setState((state) => {
    state.signupForm = {} as SignupForm;
  });
}

export function updateUser(user: TUser) {
  return useAuthStore.setState(
    (state) => {
      state.user = user;
    },
    false,
    { type: 'auth-store/updateUser', user },
  );
}

export function resetAuthStore(showAuth: boolean = false) {
  return useAuthStore.setState(() => ({...initState, showAuth}));
}

export function setAuth(user: TUser) {
  return useAuthStore.setState((state) => {
    state.user = user;
  });
}
